import { Box, Button, FormHelperText, IconButton, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import CancelIcon from '@mui/icons-material/Cancel';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useFileUpload } from 'shared/hooks/use-file-upload';

type ContractFormFileUploadFieldProps = {
  name: `${string}` | `${string}.${string}` | `${string}.${number}`;
  accept: string;
  isMultiple?: boolean;
  rules?: Object;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

export function FileUpload({
  name,
  disabled = false,
  accept,
  isMultiple,
  rules,
  error,
  helperText,
}: ContractFormFileUploadFieldProps) {
  const {
    handleRemoveFile,
    handleChange,
    files,
    handleDragOver,
    handleDrop,
    handleDragEnter,
    trigger,
    inputRef,
    clear,
  } = useFileUpload();
  const { setError } = useFormContext();

  const {
    field,
    formState: { isSubmitSuccessful },
  } = useController({
    name,
    rules,
  });
  const [defaultFieldValue] = useState<typeof field.value>(field.value);

  const hasFiles = !!files?.length;

  useEffect(() => {
    if (isSubmitSuccessful) {
      clear();
    }
    if (field.value === defaultFieldValue) {
      //when user cancels edit quote
      clear();
    }
  }, [isSubmitSuccessful, field.value, defaultFieldValue, clear]);

  useEffect(() => {
    if (field.value !== files) {
      field.onChange(files);
    }
  }, [field, files]);

  useEffect(() => {
    if (!!files?.length) {
      setError(name, { message: 'File is required' });
    }
  }, [files?.length, name, setError]);

  useEffect(() => {
    return () => clear();
  }, [clear]);

  return (
    <>
      {disabled ? (
        <Typography>Edit quote to upload files</Typography>
      ) : (
        <input
          disabled={disabled}
          id={name}
          ref={inputRef}
          hidden
          type={'file'}
          multiple={!!isMultiple}
          accept={accept}
          onChange={handleChange}
        />
      )}
      <div onDragOver={handleDragOver} onDrop={handleDrop} onDragEnter={handleDragEnter}>
        <Button onClick={trigger} startIcon={<FileUploadIcon />} disabled={disabled}>
          {`Select File${isMultiple ? '(s)' : ''}`}
        </Button>
        {error && <FormHelperText error={error}>{helperText}</FormHelperText>}
      </div>
      {!!files && hasFiles && (
        <>
          {files.map(file => (
            <Box display='flex' alignItems='center' key={file.name}>
              <Box>
                <Typography>{file.name}</Typography>
              </Box>
              <Box sx={{ paddingLeft: 1 }}>
                <IconButton
                  title='Remove file'
                  onClick={() => {
                    handleRemoveFile(file);
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
        </>
      )}
    </>
  );
}
